import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { FieldVisitStatus } from "@/constants/entity-status";
import { AppRoutes } from "@/constants/routes";
import { useRoles } from "@/hooks/useRoles";
import { Flex, useDisclosure } from "@chakra-ui/react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { FieldValues, useForm, useWatch } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import {
  assignTechnicianFieldVisitHandler,
  closeFieldVisitHandler,
  fetchFieldVisitDetailHandler,
  fetchTechniciansListHandler,
  markDoneFieldVisitHandler,
  rejectFieldVisitHandler,
} from "./handler-functions";
import { FieldVisitDetail } from "../schema/field-visit.schema";
import { FieldVisitDetailActions } from "@/components/common/section-components/requests/field-visit/FieldVisitDetailActions";
import { FieldVisitMainData } from "@/components/common/section-components/requests/field-visit/FieldVisitMainData";
import { RequestTypes } from "@/constants/enums";
import { RejectRequestModal } from "@/components/modal/RejectRequestModal";
import { FieldVisitRejectMotive } from "@/components/common/section-components/requests/field-visit/FieldVisitRejectMotive";
import { FieldVisitTechnicianObservation } from "@/components/common/section-components/requests/field-visit/FieldVisitTechnicianObservation";
import { FieldVisitRequesterObservation } from "@/components/common/section-components/requests/field-visit/FieldVisitRequesterObservation";

dayjs.extend(utc);
dayjs.extend(timezone);

export const FieldVisitDetailPage = () => {
  const [isActive, setIsActive] = useState<boolean>(false);

  useEffect(() => {
    setIsActive(true);
  }, []);

  const { id } = useParams();
  const navigate = useNavigate();
  const { fetchPermissions } = useRoles();

  const [detail, setDetail] = useState<FieldVisitDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState<boolean>(false);

  const [loadingAssign, setLoadingAssign] = useState<boolean>(false);
  const [loadingDone, setLoadingDone] = useState<boolean>(false);
  const [loadingClose, setLoadingClose] = useState<boolean>(false);
  const [loadingReject, setLoadingReject] = useState<boolean>(false);

  const { open: openRejectModal, setOpen: setOpenRejectModal } =
    useDisclosure();
  const { open: openAssignModal, setOpen: setOpenAssignModal } =
    useDisclosure();
  const { open: openDoneModal, setOpen: setOpenDoneModal } = useDisclosure();
  const { open: openCloseModal, setOpen: setOpenCloseModal } = useDisclosure();

  const fetchDetail = useCallback(async () => {
    await fetchFieldVisitDetailHandler({
      id,
      setLoading: setLoadingDetail,
      setDetail,
      navigate,
      fetchPermissions,
    });
  }, [fetchPermissions, id, navigate]);

  const refreshDetailSilently = useCallback(async () => {
    await fetchFieldVisitDetailHandler({
      id,
      setDetail,
      navigate,
      fetchPermissions,
    });
  }, [fetchPermissions, id, navigate]);

  useEffect(() => {
    if (id && isActive) fetchDetail();
  }, [fetchDetail, id, isActive]);

  const status = detail?.estado as FieldVisitStatus | undefined;

  const canReject =
    Boolean(status) &&
    status !== FieldVisitStatus.ASSIGNED &&
    status !== FieldVisitStatus.DONE &&
    status !== FieldVisitStatus.CLOSED;

  const canAssign = status === FieldVisitStatus.PENDING_ASSIGNATION;
  const canMarkDone = status === FieldVisitStatus.ASSIGNED;
  const canClose = status === FieldVisitStatus.DONE;

  const showRejectMessage = status === FieldVisitStatus.REJECTED;
  const isReadOnly = status === FieldVisitStatus.REJECTED;

  const actionButtons = useMemo(() => {
    return (
      <FieldVisitDetailActions
        loadingAssign={loadingAssign}
        loadingDone={loadingDone}
        loadingClose={loadingClose}
        loadingReject={loadingReject}
        onAssign={() => setOpenAssignModal(true)}
        onDone={() => setOpenDoneModal(true)}
        onClose={() => setOpenCloseModal(true)}
        onReject={() => setOpenRejectModal(true)}
        showAssign={canAssign}
        showDone={canMarkDone}
        showClose={canClose}
        showReject={canReject}
      />
    );
  }, [
    canAssign,
    canMarkDone,
    canClose,
    canReject,
    loadingAssign,
    loadingDone,
    loadingClose,
    loadingReject,
    setOpenAssignModal,
    setOpenCloseModal,
    setOpenDoneModal,
    setOpenRejectModal,
  ]);

  const handleBack = () => navigate(AppRoutes.FIELD_VISIT);

  const handleReject = async (event?: FieldValues) => {
    if (!id) return;

    const motivoRechazo = String(event?.request_detail ?? "");
    if (!motivoRechazo) return;

    await rejectFieldVisitHandler({
      id,
      motivoRechazo,
      setLoading: setLoadingReject,
      navigate,
      fetchPermissions,
      onSuccess: async () => {
        setOpenRejectModal(false);
        await refreshDetailSilently();
      },
    });
  };

  const [technicianOptions, setTechnicianOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [loadingTechnicians, setLoadingTechnicians] = useState<boolean>(false);

  useEffect(() => {
    if (!openAssignModal) return;

    let cancelled = false;

    fetchTechniciansListHandler({
      isCancelled: () => cancelled,
      setLoading: setLoadingTechnicians,
      setOptions: setTechnicianOptions,
    });

    return () => {
      cancelled = true;
    };
  }, [fetchTechniciansListHandler, openAssignModal]);

  const {
    control: assignControl,
    handleSubmit: handleAssignSubmit,
    reset: resetAssign,
  } = useForm<FieldValues>({ defaultValues: { tecnico: [] } });

  const assignTechnician = async (event: FieldValues) => {
    if (!id) return;
    const selected = Array.isArray(event?.tecnico) ? event.tecnico?.[0] : null;
    if (!selected) return;

    await assignTechnicianFieldVisitHandler({
      id,
      idTecnico: Number(selected),
      setLoading: setLoadingAssign,
      navigate,
      fetchPermissions,
      onSuccess: async () => {
        setOpenAssignModal(false);
        resetAssign();
        await refreshDetailSilently();
      },
    });
  };

  const {
    control: doneControl,
    handleSubmit: handleDoneSubmit,
    reset: resetDone,
  } = useForm<FieldValues>({ defaultValues: { fechaFinal: undefined } });

  const markDone = async (event: FieldValues) => {
    if (!id) return;

    const fechaFinal = event?.fechaFinal
      ? dayjs(event.fechaFinal).format("YYYY-MM-DD")
      : undefined;

    if (!fechaFinal) return;

    await markDoneFieldVisitHandler({
      id,
      fechaFinal,
      setLoading: setLoadingDone,
      navigate,
      fetchPermissions,
      onSuccess: async () => {
        setOpenDoneModal(false);
        resetDone();
        await refreshDetailSilently();
      },
    });
  };

  const {
    control: closeControl,
    handleSubmit: handleCloseSubmit,
    register: registerClose,
    formState: { errors: closeErrors },
    reset: resetClose,
  } = useForm<FieldValues>({ defaultValues: { observacionTecnico: "" } });

  const watchedObsTecnico = useWatch({
    control: closeControl,
    name: "observacionTecnico",
  });

  const closeVisit = async (event: FieldValues) => {
    if (!id) return;

    const observacionTecnico = String(event?.observacionTecnico ?? "").trim();
    if (!observacionTecnico) return;

    await closeFieldVisitHandler({
      id,
      observacionTecnico,
      setLoading: setLoadingClose,
      navigate,
      fetchPermissions,
      onSuccess: async () => {
        setOpenCloseModal(false);
        resetClose();
        await refreshDetailSilently();
      },
    });
  };

  return (
    <Flex
      flexDir="column"
      paddingTop="20px"
      gap="12px"
      height="100%"
      minHeight={0}
    >
      <SectionHeader
        title="Visita a Campo"
        onBack={handleBack}
        backButton
        loading={loadingDetail}
      />

      {!loadingDetail && (
        <Flex
          flex="1"
          minHeight={0}
          overflowY="auto"
          flexDir="column"
          gap="12px"
          paddingRight="8px"
          paddingBottom="16px"
        >
          {!isReadOnly && (
            <Flex justifyContent="flex-end">{actionButtons}</Flex>
          )}

          <FieldVisitMainData
            titular={detail?.titular}
            contacto={detail?.contacto}
            destino={detail?.destino}
            fechaDeseada={detail?.fechaDeseada}
            fechaFinal={detail?.fechaFinal}
            motivoVisita={detail?.motivoVisita}
            tecnicoAsignado={detail?.tecnicoAsignado}
            estadoDescripcion={detail?.estado}
          />

          <FieldVisitRequesterObservation detail={detail} />

          {detail?.estado === FieldVisitStatus.CLOSED && <FieldVisitTechnicianObservation detail={detail} />}

          {showRejectMessage && Boolean(detail?.mensajeRechazo?.texto) && (
            <FieldVisitRejectMotive detail={detail} />
          )}
        </Flex>
      )}

      <RejectRequestModal
        open={openRejectModal}
        setOpen={setOpenRejectModal}
        handleAccept={handleReject}
        loadingAccept={loadingReject}
        idTipoSolicitud={RequestTypes.VisitaCampo}
        entityName="visita a campo"
      />

      {openAssignModal && (
        <Suspense fallback={null}>
          <AssignFieldVisitModal
            open={openAssignModal}
            setOpen={setOpenAssignModal}
            resetAssign={resetAssign}
            handleAssignSubmit={handleAssignSubmit}
            assignTechnician={assignTechnician}
            assignControl={assignControl}
            technicianOptions={technicianOptions}
            loadingTechnicians={loadingTechnicians}
            loadingAssign={loadingAssign}
          />
        </Suspense>
      )}

      {openDoneModal && (
        <Suspense fallback={null}>
          <MarkDoneFieldVisitModal
            open={openDoneModal}
            setOpen={setOpenDoneModal}
            resetDone={resetDone}
            handleDoneSubmit={handleDoneSubmit}
            markDone={markDone}
            doneControl={doneControl}
            loadingDone={loadingDone}
          />
        </Suspense>
      )}

      {openCloseModal && (
        <Suspense fallback={null}>
          <CloseFieldVisitModal
            open={openCloseModal}
            setOpen={setOpenCloseModal}
            resetClose={resetClose}
            handleCloseSubmit={handleCloseSubmit}
            closeVisit={closeVisit}
            registerClose={registerClose}
            closeErrors={closeErrors}
            watchedObsTecnico={watchedObsTecnico}
            loadingClose={loadingClose}
          />
        </Suspense>
      )}
    </Flex>
  );
};

const AssignFieldVisitModal = lazy(() =>
  import("./AssignFieldVisitModal").then((m) => ({
    default: m.AssignFieldVisitModal,
  })),
);
const MarkDoneFieldVisitModal = lazy(() =>
  import("./MarkDoneFieldVisitModal").then((m) => ({
    default: m.MarkDoneFieldVisitModal,
  })),
);
const CloseFieldVisitModal = lazy(() =>
  import("./CloseFieldVisitModal").then((m) => ({
    default: m.CloseFieldVisitModal,
  })),
);

